import { BookingConfirmationIntro } from '@components/Confirmation/BookingConfirmationIntro';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { PageHeading } from '@components/Layouts/PageHeading';
import { t } from '@lingui/core/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { cn } from 'noddi-ui-common';
import { LoadingScreen } from 'noddi-ui-web';
import { customerRoutes, getIsAwaitingCustomerPayment } from 'noddi-util';
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { BookingConfirmation } from './BookingConfirmation';

const DISPLAY_MS = 2500;
const FADE_MS = 500;

type Phases = 'visible' | 'fading' | 'hidden';

export const ConfirmationPage = () => {
  const { slug } = useParams();
  const [phase, setPhase] = useState<Phases>('visible');

  const {
    data: booking,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug: slug! }
  });

  // Start the fade-out only once the confirmation is actually on screen, or a slow
  // booking fetch burns the timers behind the loading screen and skips the intro.
  useEffect(() => {
    if (isPending) {
      return;
    }
    const t1 = setTimeout(() => setPhase('fading'), DISPLAY_MS);
    const t2 = setTimeout(() => setPhase('hidden'), DISPLAY_MS + FADE_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isPending]);

  if (isPending) {
    return <LoadingScreen />;
  }
  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  // A booking awaiting payment is NOT confirmed — no confetti, no "what happens
  // next", no order confirmation promise. We land here when the payment session
  // could not be started after create; the booking page owns that state (payment
  // required banner, missing billing details, pay-to-confirm CTA).
  if (getIsAwaitingCustomerPayment(booking.status.value)) {
    return <Navigate to={customerRoutes.bookingInfo.getPath({ slug: slug! })} replace />;
  }

  const confirmedTitle = t`Booking confirmed!`;
  const heading =
    phase === 'hidden' ? (
      <PageHeading className='text-2xl font-bold text-primary'>{confirmedTitle}</PageHeading>
    ) : (
      <h1 tabIndex={-1} className='text-2xl font-bold text-primary focus:outline-hidden'>
        {confirmedTitle}
      </h1>
    );

  return (
    <>
      {phase !== 'hidden' && (
        <BookingConfirmationIntro
          className={cn(phase === 'visible' ? 'opacity-100' : 'opacity-0')}
          iconClassName={cn(phase === 'visible' ? 'scale-100' : 'scale-95')}
          title={confirmedTitle}
        />
      )}
      <div
        className={cn(
          'flex flex-col gap-5 transition-opacity duration-500',
          phase === 'hidden' ? 'opacity-100' : 'opacity-0'
        )}
        inert={phase !== 'hidden'}
        aria-hidden={phase !== 'hidden'}
      >
        <ContentWrapperCustomerApp hideBackButton>
          <BookingConfirmation bookingDetails={booking} heading={heading} />
        </ContentWrapperCustomerApp>
      </div>
    </>
  );
};
